import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ArrowRight, Globe, Shield, TrendingUp, Users, Zap, Star,
  UserPlus, CreditCard, Gift, Rocket,
} from 'lucide-react';
import { ImageWithFallback } from '../ui/image-with-fallback';

const ICON_MAP = {
  'Unlimited Access': Globe,
  'Exclusive Discounts': Star,
  'Endless Opportunities': Zap,
  'Transparency': Shield,
  'Accessibility': Globe,
  'Community': Users,
  'Innovation': Zap,
  'Integrity': Shield,
  'Empowerment': TrendingUp,
};

const STEP_COLORS = ['#00E5FF', '#007AFF', '#00E676', '#FFEA00'];
const STEP_ICONS = [UserPlus, CreditCard, Gift, Rocket];

function bgStyle(section) {
  if (!section.background_image) return {};
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const imgUrl = section.background_image.startsWith('/api') ? `${backendUrl}${section.background_image}` : section.background_image;
  return {
    backgroundImage: `url(${imgUrl})`,
    backgroundSize: section.background_size || 'cover',
    backgroundPosition: section.background_position || 'center center',
    backgroundAttachment: section.background_attachment || 'scroll',
    backgroundRepeat: 'no-repeat',
  };
}

/**
 * Renders a single CMS-defined section. Pure presentational — `onCta`
 * fires when the primary CTA button is clicked.
 */
export function LandingCmsSection({ section, idx, onCta }) {
  const style = bgStyle(section);

  switch (section.section_type) {
    case 'hero':
      return (
        <section className="relative overflow-hidden" style={style}>
          {section.background_image && <div className="absolute inset-0" style={{ backgroundColor: section.overlay_color || '#000B18', opacity: section.overlay_opacity ?? 0.8 }} />}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-32">
            <div className="max-w-3xl">
              {section.subtitle && (
                <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30 mb-4 sm:mb-6 px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm">{section.subtitle}</Badge>
              )}
              <h1 className="font-outfit text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 sm:mb-6 break-words" data-testid="hero-title">{section.title}</h1>
              {section.content && <p className="text-base sm:text-lg text-[#A0B4CC] max-w-2xl mb-6 sm:mb-8 leading-relaxed">{section.content}</p>}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
                <Button onClick={onCta} className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3 h-auto w-full sm:w-auto" data-testid="hero-cta-btn">
                  Get Started <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Link to="/marketplace" className="w-full sm:w-auto">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3 h-auto w-full sm:w-auto" data-testid="hero-explore-btn">Explore Marketplace</Button>
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute top-20 right-0 w-96 h-96 bg-[#00E5FF]/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-[#007AFF]/5 rounded-full blur-3xl pointer-events-none" />
        </section>
      );

    case 'features':
      return (
        <section className="py-12 sm:py-16 md:py-24" style={style}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {section.title && (
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="font-outfit text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 break-words" data-testid="features-title">{section.title}</h2>
                {section.subtitle && <p className="text-[#A0B4CC] text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-2">{section.subtitle}</p>}
              </div>
            )}
            <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 md:grid-cols-3">
              {(section.items || []).map((item, i) => {
                const ItemIcon = ICON_MAP[item.title] || Globe;
                return (
                  <Card key={item.title || item.id || `item-${i}`} className="bg-[#041A3B]/80 border-white/10 hover:border-[#00E5FF]/30 transition-all duration-300 group">
                    <CardContent className="p-5 sm:p-6">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#00E5FF]/20 transition-colors">
                        <ItemIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#00E5FF]" />
                      </div>
                      <h3 className="font-outfit text-base sm:text-lg font-semibold text-white mb-2 break-words">{item.title || item}</h3>
                      {item.description && <p className="text-[#A0B4CC] text-sm leading-relaxed">{item.description}</p>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      );

    case 'content':
      return (
        <section className="relative py-12 sm:py-16 md:py-24" style={style}>
          {section.background_image && <div className="absolute inset-0" style={{ backgroundColor: section.overlay_color || '#000B18', opacity: section.overlay_opacity ?? 0.8 }} />}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              {section.title && <h2 className="font-outfit text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 break-words">{section.title}</h2>}
              {section.subtitle && <p className="text-[#00E5FF] text-base sm:text-lg font-medium mb-4 sm:mb-6">{section.subtitle}</p>}
              {section.content && (
                <div className="text-[#A0B4CC] text-sm sm:text-base text-left leading-relaxed whitespace-pre-line max-w-2xl mx-auto break-words">{section.content}</div>
              )}
            </div>
          </div>
        </section>
      );

    case 'cta':
      return (
        <section className="py-12 sm:py-16 md:py-24" style={style}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="p-6 sm:p-8 md:p-12 rounded-2xl bg-gradient-to-r from-[#00E5FF]/10 to-[#007AFF]/10 border border-[#00E5FF]/20">
              <h2 className="font-outfit text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 break-words">{section.title}</h2>
              {section.subtitle && <p className="text-[#A0B4CC] text-sm sm:text-base md:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">{section.subtitle}</p>}
              <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3 sm:gap-4">
                <Button onClick={onCta} className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3 h-auto w-full sm:w-auto" data-testid="cta-get-started-btn">
                  Get Started Now <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Link to="/pricing" className="w-full sm:w-auto">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3 h-auto w-full sm:w-auto">View Pricing</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      );

    case 'gallery':
      return (
        <section className="py-12 sm:py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {section.title && <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8 text-center break-words">{section.title}</h2>}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-3 sm:gap-4">
              {(section.items || []).map((item, i) => (
                <div key={item.title || item.id || `item-${i}`} className="aspect-video rounded-xl overflow-hidden bg-[#041A3B]">
                  <ImageWithFallback src={item.url || item.image || item} alt={item.title || ''} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'faq':
      return (
        <section className="py-12 sm:py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {section.title && <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8 text-center break-words">{section.title}</h2>}
            <div className="space-y-3 sm:space-y-4">
              {(section.items || []).map((item, i) => (
                <Card key={item.title || item.id || `item-${i}`} className="bg-[#041A3B] border-white/10">
                  <CardContent className="p-5 sm:p-6">
                    <h4 className="font-medium text-white mb-2 text-sm sm:text-base break-words">{item.question || item.title}</h4>
                    <p className="text-[#A0B4CC] text-sm break-words">{item.answer || item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      );

    case 'steps':
      return (
        <section className="py-14 sm:py-20 md:py-28 relative overflow-hidden" style={style}>
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-[#00E5FF]/3 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[#007AFF]/3 rounded-full blur-[120px] pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {section.title && (
              <div className="text-center mb-10 sm:mb-16">
                <span className="inline-block text-[#00E5FF] text-[10px] sm:text-xs font-semibold tracking-[0.3em] uppercase mb-3 sm:mb-4">{section.subtitle || 'The Journey'}</span>
                <h2 className="font-outfit text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white break-words">{section.title}</h2>
              </div>
            )}
            <div className="hidden lg:block absolute top-1/2 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-4">
              {(section.items || []).map((item, i) => {
                const color = STEP_COLORS[i % STEP_COLORS.length];
                const StepIcon = STEP_ICONS[i % STEP_ICONS.length];
                const yOffset = [0, 8, 2, 10][i % 4];
                return (
                  <div key={item.title || item.id || `item-${i}`} className="group relative" style={{ transform: `translateY(${yOffset * 4}px)` }}>
                    <div className="relative p-6 rounded-2xl bg-gradient-to-b from-[#041A3B] to-[#000B18] border border-white/[0.06] hover:border-opacity-30 transition-all duration-500 h-full">
                      <div className="absolute -top-3 -left-1 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: color, color: i === 1 ? '#fff' : '#000B18', boxShadow: `0 0 20px ${color}40` }}>{i + 1}</div>
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-all duration-500" style={{ backgroundColor: `${color}12`, borderColor: `${color}18`, borderWidth: '1px' }}>
                        <StepIcon className="w-6 h-6" style={{ color }} />
                      </div>
                      <h3 className="font-outfit text-lg font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-[#5C7599] text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      );

    default:
      return (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {section.title && <h2 className="font-outfit text-3xl font-bold text-white mb-4">{section.title}</h2>}
            {section.content && <p className="text-[#A0B4CC] whitespace-pre-line">{section.content}</p>}
          </div>
        </section>
      );
  }
}
